import './index.scss'
import text from './text.json'
import {useDispatch, useSelector} from 'react-redux'
import LoaderScreen from '../loader'
import {Link, NavLink, useLocation, useNavigate} from 'react-router-dom'
import {routes} from '../../../routes'
import Logo from '../../../assets/images/logo.svg'
import ClosedLogo from '../../../assets/images/logo-closed.svg'
import classNames from 'classnames'
import {Fragment, useEffect, useRef, useState} from 'react'
import {
    ArrowDownIcon,
    ArrowLeftIcon,
    ArrowUpIcon,
    BillingsIcon,
    HeartBeatsIcon,
    LogoutIcon,
    NotificationIcon,
    UptimeMonitoringIcon,
    ClosedIncidentManagementIcon,
    UserIcon,
    UsersIcon,
    StatuspageIcon,
    LogManagementIcon
} from '../../../icons'
import {Collapse} from 'react-collapse'
import CustomerHelper from '../../../utils/customer'
import Spinner from '../../core/spinner'
import useOnClickOutside from '../../../utils/hooks/useOutsideClick'
import {userLogOut} from '../../../stores/auth'
import {setSidebarIsActive, toggleSidebar} from '../../../stores/sidebar'
import Badge from '../../core/badge'
import {isNewFeatureEnabled, isSelfHosted} from '../../../utils/newFeature'
import TextHelper from '../../../utils/text'
import DarkModeSwitcher from '../../core/darkModeSwitcher'

const MIN_VALUE = 1

const navElement = (icon, title, key, children, isNew = false, isBeta = false) => {
    return {icon, title, key, children, isNew, isBeta}
}

const subNavElement = (title, url, internalPagesUrls = [], isNew = false, isBeta = false) => {
    return {title, url, internalPagesUrls, isNew, isBeta}
}

const Sidebar = ({trialMessage, noPlan}) => {
    const [accountMenuIsActive, setAccountMenuIsActive] = useState(false)
    const accountMenuRef = useRef(null)
    const sidebarRef = useRef(null)
    const [collapse, setCollapse] = useState({
        'monitoring': false,
        teams: false,
        notifications: false,
        heartbeats: false,
        'status-pages': false,
        'incident-management': false
    })
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated)
    const logoutIsProcessing = useSelector(state => state.auth.logoutIsProcessing)
    const customer = useSelector(state => state.sidebar.customer)
    const sidebarIsActive = useSelector(state => state.sidebar.sidebarIsActive)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const sidebarClassName = classNames({
        sidebar: true,
        'no-plan-selected': noPlan,
        closed: !sidebarIsActive
    })

    const mobileWidth = 768

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const stopPropagation = e => {
        e.stopPropagation()
    }

    const logOut = () => {
        dispatch(userLogOut())
    }

    const navArr = [
        navElement(<UptimeMonitoringIcon/>, 'Monitoring', 'uptime-monitoring',
            [
                subNavElement(routes.resources.title, routes.resources.path, [routes.monitoring.path, routes.resource_integrations.path]),
                subNavElement(routes.server_monitoring.title, routes.server_monitoring.path, [routes.create_server_monitoring.path, routes.edit_server_monitoring.path, routes.server_resource.path]),
                subNavElement(routes.plans.title, '/plans/#monitoring', ['#monitoring']),
                subNavElement(routes.usage.title, '/usage/#monitoring', ['#monitoring'])
            ]),
        (isSelfHosted() || process.env.REACT_APP_ENV !== 'prod') &&  navElement(<LogManagementIcon customClassname='log-management-icon' />, 'Log management', 'log-management',
            [
                subNavElement(routes.log_groups_list.title, routes.log_groups_list.path),
                subNavElement(routes.logs_filtered_view.title, routes.logs_filtered_view.path)
            ], false, true),
        navElement(<StatuspageIcon/>, 'Status Pages', 'status-pages',
            [
                subNavElement('Pages', routes.statuspages.path, [routes.create_statuspage.path, routes.edit_statuspage.path]),
                subNavElement('Subscriptions', routes.statuspage_subscriptions.path),
                subNavElement(routes.plans.title, '/plans/#status-pages', ['#status-pages']),
                ...isNewFeatureEnabled() ? [subNavElement(routes.usage.title, '/usage/#status-pages', ['#status-pages'])] : []
            ],
            false, false),
        navElement(<ClosedIncidentManagementIcon/>, 'Incident management', 'incident-management',
            [
                subNavElement(routes.incidents.title, routes.incidents.path),
                subNavElement(routes.rulesets.title, routes.rulesets.path, [routes.create_ruleset.path]),
                subNavElement(routes.on_call_scheduling.title, routes.on_call_scheduling.path),
                subNavElement(routes.plans.title, '/plans/#incident-management', ['#incident-management']),
                subNavElement(routes.usage.title, '/usage/#incident-management', ['#incident-management'])
            ],
            false, true),
        navElement(<HeartBeatsIcon/>, 'Heartbeats', 'heartbeats',
            [
                subNavElement(routes.jobs.title, routes.jobs.path, [routes.job.path]),
                subNavElement(routes.job_events.title, routes.job_events.path, [routes.job_events.path])
            ]),
        navElement(<UsersIcon/>, !isSelfHosted() ? 'Teams :intermediarySymbol Integrations' : 'Teams', 'teams', [
            subNavElement(routes.teams.title, routes.teams.path, [routes.members.path]),
            subNavElement(routes.services.title, routes.services.path),
            ...!isSelfHosted() ? [subNavElement(routes.integrations.title, routes.integrations.path)] : []

        ]),
        navElement(<NotificationIcon/>, 'Notifications', 'notifications', [
            subNavElement(routes.notifications.title, routes.notifications.path),
            subNavElement(routes.team_channels.title, routes.team_channels.path),
            ...isNewFeatureEnabled() && !isSelfHosted() ? [subNavElement(routes.automations.title, routes.automations.path, [routes.automation_designer.path], false, true)] : []
        ])
    ]

    const accountMenu = [
        navElement(<UserIcon/>, 'Profile', 'profile', [
            !isSelfHosted() && subNavElement(routes.redeem_coupon.title, routes.redeem_coupon.path),
            subNavElement(routes.apply_voucher.title, routes.apply_voucher.path),
            subNavElement(routes.settings.title, routes.settings.path),
            subNavElement(routes.change_password.title, routes.change_password.path),
            subNavElement(routes.change_username.title, routes.change_username.path),
            subNavElement(routes.delete_account.title, routes.delete_account.path)
        ]),
        !isSelfHosted() && navElement(<BillingsIcon/>, 'Billings', 'billings', [
            subNavElement(routes.subscriptions.title, routes.subscriptions.path),
            subNavElement(routes.payment_methods.title, routes.payment_methods.path)
        ])
    ]

    const getNavElementClassName = (item) => {
        const {key, children} = item
        const {pathname} = location
        const isActive = children.some(item => item.url === pathname) ||
            children.some(item => getPathNameMenu(item))
        return classNames({
            'nav-item': true,
            active: isActive && collapse?.[key]
        })
    }

    const getPathNameMenu = (item) => {
        return item.internalPagesUrls?.some(internalUrl => {
            return location.pathname.split('/')?.[MIN_VALUE] === internalUrl?.split('/')?.[MIN_VALUE] || (location.hash.includes(internalUrl) && location?.pathname.includes(item?.url))
        }) || item?.url === location.pathname + location.hash
    }

    const getSubItemClassName = (item) => {
        return classNames({
            active: getPathNameMenu(item)
        })
    }

    const generateSubMenu = navItem => {
        const {children} = navItem
        return <ul className="sub-menu">
            {children.map(item => (
                <li className="sub-item" key={item.title}>
                    <Link className={getSubItemClassName(item)}
                        to={item.url}>
                        {item.title}
                        {item.isNew && <Badge color="primary">New</Badge>}
                        {item.isBeta && <Badge color="secondary">beta</Badge>}
                    </Link>
                </li>
            ))}
        </ul>
    }

    const handleCollapse = item => {
        if (sidebarIsActive) {
            setCollapse(prev => ({
                ...prev,
                [item.key]: !prev?.[item.key]
            }))
            return
        }
        navigate(item.children[0].url)
    }

    useEffect(() => {
        const pathMenuItem = navArr.find(navArrEl => navArrEl?.children?.some(item => getPathNameMenu(item)))

        setCollapse(prev => ({
            ...prev,
            [pathMenuItem?.key]: true
        }))
    }, [location?.pathname, location?.hash])

    const generateMenu = () => {
        return <nav className="navbar">
            <ul className="nav">
                {navArr.map((item) => (
                    item && <Fragment key={item.key}>
                        <li onClick={() => handleCollapse(item)}
                            className={getNavElementClassName(item)}>
                            <div className="d-flex">
                                <span className="nav-icon">{item.icon}</span>
                                <span className="nav-title">
                                    <span>{TextHelper.createSectionTitle(item.title)}</span>
                                    {item.isNew && <Badge color="primary">New</Badge>}
                                    {item.isBeta && <Badge color="secondary">beta</Badge>}
                                </span>
                            </div>
                            <span className={classNames({
                                icon: true,
                                active: collapse?.[item.key]
                            })}>
                                <ArrowDownIcon/>
                            </span>
                        </li>
                        <Collapse isOpened={collapse?.[item.key]}>
                            {generateSubMenu(item)}
                        </Collapse>
                    </Fragment>
                ))}
            </ul>
        </nav>
    }

    const generateAccountMenu = () => {
        return (
            <div
                className={classNames({
                    'account-menu': true,
                    active: accountMenuIsActive
                })}>
                {accountMenu.map((item, index) => (
                    item && <div key={index}>
                        <li className="account-menu-item">
                            <span className="d-flex">
                                {item.icon}
                            </span>
                            <span>{item.title}</span>
                        </li>
                        <ul className="account-menu-sub-items">
                            {item.children.map((menuItem, index) => (
                                menuItem && <li key={index} className="open-list-item">
                                    <NavLink className="open-sub-link" to={menuItem.url}>{menuItem.title}</NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
                <li className="account-menu-single-item" onClick={stopPropagation}>
                    <div className="account-menu-link-item" onClick={logOut}>
                        <span className="d-flex logout">
                            <LogoutIcon/>
                        </span>
                        <span>{text.log_out}</span>
                        {logoutIsProcessing && <Spinner/>}
                    </div>
                </li>
            </div>
        )
    }

    useOnClickOutside(accountMenuRef, () => setAccountMenuIsActive(false))
    useOnClickOutside(sidebarRef, (e) => {
        const className = e.target.getAttribute('class')
        if (mobileWidth >= window.innerWidth && sidebarIsActive && className !== 'responsive-menu-icon') {
            dispatch(setSidebarIsActive(false))
        }
    })

    useEffect(() => {
        if (mobileWidth >= window.innerWidth) {
            dispatch(setSidebarIsActive(false))
        }
    }, [location])

    return isAuthenticated ? (
        <aside
            ref={sidebarRef}
            className={sidebarClassName}>
            <div className={`d-flex justify-content-between ${!sidebarIsActive ? 'flex-column' : ''}`}>
                <Link to={routes.resources.path} className="logo">
                    <img width="116" height="31" src={sidebarIsActive ? Logo : ClosedLogo} alt="Pinghome"/>
                </Link>
                <DarkModeSwitcher/>
            </div>
            <div className={classNames('toggle-btn', {
                dark: isDarkMode
            })} onClick={() => dispatch(toggleSidebar())}>
                <span>
                    <ArrowLeftIcon/>
                </span>
            </div>
            {generateMenu()}
            <div className="account"
                ref={accountMenuRef}
                onClick={() => setAccountMenuIsActive(prev => !prev)}>
                <div className="responsive-account-menu">
                    <UserIcon/>
                    {generateAccountMenu()}
                </div>
                <div className="account-info">
                    <span
                        className="cover">{CustomerHelper.getUserFullNameFirstChars(customer)}</span>
                    <span className="customer-name">
                        <span className="customer-fullname">{CustomerHelper.getUserFullName(customer)}</span>
                        {trialMessage !== null && <span className="trial-info" dangerouslySetInnerHTML={{__html: trialMessage}}/>}
                    </span>
                    {generateAccountMenu()}
                </div>
                <div className="arrows">
                    <span>
                        <ArrowUpIcon/>
                    </span>
                    <span>
                        <ArrowDownIcon/>
                    </span>
                </div>
            </div>
        </aside>
    ) : <LoaderScreen/>
}

export default Sidebar
